import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Select } from '@components';
import { PurchaseNumberRequest } from '../types';
import { useCountryCodes } from '../hooks/useCountryCodes';
import { useFreeSwitchBlocks } from '../hooks/useFreeSwitchBlocks';
import { ApiClient } from '@utils/apiClient';

interface BuyNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchase: (data: PurchaseNumberRequest) => Promise<void>;
}

export const BuyNumberModal: React.FC<BuyNumberModalProps> = ({ isOpen, onClose, onPurchase }) => {
  const { t } = useTranslation();
  const { countryCodes, loading: countryCodesLoading } = useCountryCodes();
  const { blocks, loading: blocksLoading, fetchBlocks } = useFreeSwitchBlocks();
  const [provider, setProvider] = useState<'twilio' | 'freeswitch' | null>(null);
  const [providerLoading, setProviderLoading] = useState(false);
  const [formData, setFormData] = useState<PurchaseNumberRequest>({ country_code: '' });
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setProviderLoading(true);
    ApiClient.get('/telephony/phone-numbers/provider_info/')
      .then(r => r.json())
      .then(data => {
        const p = data?.provider ?? 'twilio';
        setProvider(p);
        if (p === 'freeswitch') fetchBlocks();
      })
      .catch(() => setProvider('twilio'))
      .finally(() => setProviderLoading(false));
  }, [isOpen, fetchBlocks]);

  const isFreeswitch = provider === 'freeswitch';

  const countryOptions = countryCodes.map(country => ({
    value: country.code,
    label: `${country.title} (${country.code_number})`,
  }));

  const blockOptions = blocks.map(b => ({
    value: b.id,
    label: b.description || b.prefix,
  }));

  const handleInputChange = (field: keyof PurchaseNumberRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (isFreeswitch) {
      if (!formData.block_id) {
        newErrors.block_id = t('phoneNumbers.prefixRequiredError');
      }
    } else {
      if (!formData.country_code?.trim()) {
        newErrors.country_code = t('phoneNumbers.countryCodeRequiredError');
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onPurchase(formData);
      setFormData({ country_code: '' });
      setErrors({});
    } catch (error) {
      console.error('Purchase failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ country_code: '' });
      setErrors({});
      onClose();
    }
  };

  const isDataLoading = providerLoading || (isFreeswitch ? blocksLoading : countryCodesLoading);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('phoneNumbers.buyPhoneNumber')} size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          {isFreeswitch ? (
            <div>
              <label
                htmlFor="block_id"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('phoneNumbers.prefixRequired')}
              </label>
              {blocks.length === 0 && !blocksLoading ? (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {t('phoneNumbers.noBlocksAvailable')}
                </p>
              ) : (
                <Select
                  id="block_id"
                  value={formData.block_id ?? ''}
                  onChange={value =>
                    handleInputChange('block_id', Array.isArray(value) ? value[0] : value)
                  }
                  options={blockOptions}
                  placeholder={t('phoneNumbers.selectPrefix')}
                  error={!!errors.block_id}
                  errorText={errors.block_id}
                  disabled={blocksLoading || loading}
                />
              )}
            </div>
          ) : (
            <div>
              <label
                htmlFor="country_code"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('phoneNumbers.countryRequired')}
              </label>
              <Select
                id="country_code"
                value={formData.country_code ?? ''}
                onChange={value =>
                  handleInputChange('country_code', Array.isArray(value) ? value[0] : value)
                }
                options={countryOptions}
                placeholder={t('phoneNumbers.selectCountry')}
                error={!!errors.country_code}
                errorText={errors.country_code}
                disabled={countryCodesLoading || loading}
              />
            </div>
          )}
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="material-icons text-blue-600 dark:text-blue-400 text-lg">info</span>
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">{t('phoneNumbers.importantInformation')}</p>
              <ul className="space-y-1 text-xs">
                <li>• {t('phoneNumbers.phoneNumbersPurchasedImmediately')}</li>
                <li>• {t('phoneNumbers.assignToAiAgents')}</li>
                <li>• {t('phoneNumbers.numbersCanBeDeleted')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
            className="flex-1"
          >
            {t('phoneNumbers.cancel')}
          </Button>
          <Button
            type="submit"
            variant="gradient-multi"
            loading={loading}
            disabled={isDataLoading || (isFreeswitch && blocks.length === 0)}
            className="flex-1"
          >
            <span className="material-icons text-sm mr-2">phone</span>
            {t('phoneNumbers.purchaseNumber')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

